import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { toast } from "react-toastify";
import Listtable from "components/common/ListTable";
import { intraoralFindingApi } from "api/intraoralFinding";
import IntraoralFindingModal from "./IntraoralFindingModal";
import { importFailures, importSummary } from "./intraoralFindingForm";

export const TABLE_HEADERS = ["Finding", "Sub Values", "Source", "Action"];
const TABLE_KEYS = ["name", "valuesLabel", "sourceLabel", "action"];

const btnStyle = {
  width: "max-content",
  padding: "0.5rem 2rem",
  color: "#194AF5",
  bgcolor: "#E6EBFF",
  boxShadow: "none",
  borderRadius: "8px",
  ":hover": { bgcolor: "#E6EBFF", color: "#194AF5", boxShadow: "none" },
};

const saveBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const IntraoralFindingTab = ({ master }) => {
  const api = useMemo(() => intraoralFindingApi(master.key), [master.key]);

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeFinding, setActiveFinding] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText.trim());
      setPageNumber(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  const fetchFindings = useCallback(() => {
    setLoading(true);

    api
      .list({
        page: pageNumber + 1,
        perPage: rowsPerPage,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      })
      .then((response) => {
        const payload = response?.data || {};
        const list = Array.isArray(payload.data) ? payload.data : [];

        setRows(
          list.map((item) => ({
            ...item,
            action: (
              <Box sx={{ display: "flex", gap: "0.25rem" }}>
                <IconButton
                  aria-label={`Edit ${item.name}`}
                  onClick={() => {
                    setActiveFinding(item);
                    setModalOpen(true);
                  }}
                >
                  <EditIcon fontSize="small" color="primary" />
                </IconButton>
                <IconButton
                  aria-label={`Delete ${item.name}`}
                  onClick={() => setDeleteTarget(item)}
                >
                  <DeleteOutlineIcon fontSize="small" color="error" />
                </IconButton>
              </Box>
            ),
          }))
        );

        const total = Number(payload?.meta?.total);
        setCount(Number.isFinite(total) ? total : list.length);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Could not load findings.");
      })
      .finally(() => setLoading(false));
  }, [api, pageNumber, rowsPerPage, debouncedSearch]);

  useEffect(() => {
    fetchFindings();
  }, [fetchFindings]);

  const handleModalClose = (reason = "save") => {
    setModalOpen(false);
    setActiveFinding({});
    if (reason !== "close") fetchFindings();
  };

  const handleDelete = async () => {
    if (!deleteTarget?._id) return;

    setBusy(true);
    try {
      await api.remove(deleteTarget._id);
      toast.success("Finding deleted successfully");

      if (rows.length === 1 && pageNumber > 0) {
        setPageNumber(pageNumber - 1);
      } else {
        fetchFindings();
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not delete this finding.");
    } finally {
      setBusy(false);
      setDeleteTarget(null);
    }
  };

  const handleDownload = async (kind, filename) => {
    setBusy(true);
    try {
      const response = await api.download(
        kind,
        kind === "export" && debouncedSearch ? { search: debouncedSearch } : {}
      );
      saveBlob(response.data, filename);
    } catch (err) {
      toast.error("Download failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleImport = async (event) => {
    const file = event?.target?.files?.[0];
    event.target.value = null;
    if (!file) return;

    setBusy(true);
    try {
      const response = await api.importFile(file);
      const summary = importSummary(response?.data);
      const failures = importFailures(response?.data);

      // Failed rows need their reasons, which a toast cannot hold.
      if (failures.length > 0) {
        setImportResult({ message: summary.message, failures });
      } else if (summary.level === "warning") {
        toast.warning(summary.message);
      } else {
        toast.success(summary.message);
      }

      setPageNumber(0);
      fetchFindings();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Import failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          padding: "1rem 0",
          flexWrap: "wrap",
        }}
      >
        <TextField
          size="small"
          label="Search findings"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          sx={{ minWidth: 260, mr: "auto" }}
        />

        <Button
          sx={btnStyle}
          variant="contained"
          disabled={busy}
          onClick={() => handleDownload("template", master.templateFile)}
        >
          Download Template
        </Button>

        <Button
          sx={btnStyle}
          variant="contained"
          disabled={busy}
          onClick={() => fileInputRef.current?.click()}
        >
          Import
        </Button>
        <input
          type="file"
          accept=".xlsx,.xls,.csv"
          ref={fileInputRef}
          hidden
          data-testid={`intraoral-${master.key}-import-input`}
          onChange={handleImport}
        />

        <Button
          sx={btnStyle}
          variant="contained"
          disabled={busy}
          onClick={() => handleDownload("export", master.exportFile)}
        >
          Export
        </Button>

        <Button
          sx={{ ...btnStyle, bgcolor: "#194AF5", color: "white", ":hover": {} }}
          variant="contained"
          disabled={busy}
          onClick={() => {
            setActiveFinding({});
            setModalOpen(true);
          }}
        >
          Add Finding
        </Button>
      </Box>

      {!loading && rows.length === 0 ? (
        <Box sx={{ py: 5, textAlign: "center", color: "#7D8DC4" }}>
          {master.emptyText}
        </Box>
      ) : (
        <Listtable
          tableheader={TABLE_HEADERS}
          tablebody={rows}
          rowsPerPage={rowsPerPage}
          page={pageNumber}
          handleChangePage={(event, newPage) => setPageNumber(newPage)}
          handleChangeRowsPerPage={(event) => {
            setRowsPerPage(Number(event.target.value));
            setPageNumber(0);
          }}
          totalCount={count}
          dataKeyName={TABLE_KEYS}
          tag={`intraoral-${master.key}`}
        />
      )}

      {modalOpen && (
        <IntraoralFindingModal
          open={modalOpen}
          onClose={handleModalClose}
          data={activeFinding}
          api={api}
          masterLabel={master.label}
        />
      )}

      <Dialog
        open={Boolean(importResult)}
        onClose={() => setImportResult(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Import complete</DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>{importResult?.message}</DialogContentText>
          <Box sx={{ fontWeight: 600, mb: 1 }}>Failed rows:</Box>
          <Box
            component="ul"
            aria-label="Failed rows"
            sx={{ listStyle: "none", p: 0, m: 0, maxHeight: 320, overflowY: "auto" }}
          >
            {(importResult?.failures ?? []).map((item, index) => (
              <Box
                component="li"
                key={`${item.row}-${item.name}-${index}`}
                sx={{ py: 1, borderBottom: "1px solid #EEE", fontSize: "0.875rem" }}
              >
                <Box sx={{ fontWeight: 600 }}>
                  {item.row ? `Row ${item.row}` : "Row"}
                  {item.name ? ` — ${item.name}` : ""}
                </Box>
                <Box sx={{ color: "#d32f2f" }}>Reason: {item.reason}</Box>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportResult(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete finding?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{deleteTarget?.name}" from{" "}
            {master.label}? It will stop appearing in every hospital's
            Intraoral Findings.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" disabled={busy}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop open={loading || busy} sx={{ zIndex: 1200, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
};

export default IntraoralFindingTab;
